import { Fragment } from 'react'
import { EntityRef } from '../../../components/EntityRef'
import { StatusPill, type StatusPillVariant } from '../../../components/StatusPill'

export interface AdjustmentHistoryRowView {
  id: string
  /** e.g. "QualifiedSpendLine" | "ProgramFiling" | "CreditInstrument" — CLAUDE.md §2 invariant #1's
   * guarded-field targets are not all the same entity; this table is target-agnostic. */
  targetType: string
  targetId: string
  targetLabel?: string
  fieldName: string
  /** Pre-formatted by the caller (a percent needs `%`, a money field needs its currency) — this
   * table renders values verbatim, never re-derives or re-formats a guarded figure. */
  priorValueDisplay: string
  newValueDisplay: string
  method: string
  reason: string
  actorLabel: string | null
  channel: string | null
  appliedAt: string
}

const METHOD_VARIANT: Record<string, StatusPillVariant> = {
  manual: 'info',
  computed: 'neutral',
  redistributed: 'warning',
  ai_accept: 'success',
  unlock: 'muted',
}

function methodLabel(method: string): string {
  return method
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')
}

/** `targetId` is intentionally unused today — every current `targetType` resolves to this same
 * Case's own Credit/Overview tab (a same-page anchor, not a second navigation); kept as a 2-arg
 * shape so a future target type that DOES deep-link (e.g. a QSL, which has no detail page of its
 * own today either) only needs a new `case` branch here, not a signature change. */
function targetHref(targetType: string): string | null {
  switch (targetType) {
    case 'ProgramFiling':
    case 'CreditInstrument':
    default:
      return null
  }
}

/**
 * New per-case component for IB-282 View 2 (docs/design/IB-282-view2-per-case-design.md §5,
 * DoR AC2.5) — the generalized "every guarded-field change on this case" audit table (CLAUDE.md §2
 * invariant #1's full envelope: who/what/when/how/why + before/after), one level up from the
 * existing QSL-only `SpendPanel.tsx#AdjustmentHistoryRow`. That component stays as-is (it is the
 * Spend tab's own per-line inline-expand, a different UI moment); this is the Analytics tab's
 * cross-target rollup — every `AdjustmentHistoryPort.AdjustmentRecord` touching THIS case's
 * `ProgramFiling`(s)/`CreditInstrument`(s)/`QualifiedSpendLine`(s) in one table, newest first.
 *
 * Presentational only — no fetch. Per the DoR's backend-dependency list (§3.2), the
 * `ProgramFiling`/`CreditInstrument` rows require a small additive `GET .../adjustments` endpoint
 * (mirroring `SpendAdjustmentHistoryController` exactly) that does not exist yet; the QSL rows are
 * real today via the existing `GET /qualified-spend-lines/{id}/adjustments`. A data-wiring pass
 * composes all three sources client-side (bounded by this case's own row counts) and sorts by
 * `appliedAt` — never a fabricated merge, never silently dropping a source that 404s (each source
 * degrades independently, same posture as every other multi-source panel in this codebase).
 */
export function AdjustmentsHistoryTable({ rows }: { rows: AdjustmentHistoryRowView[] }) {
  if (rows.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No adjustments recorded on this case yet.</p>
  }

  return (
    <table className="iw-table" aria-label="Adjustments history">
      <thead>
        <tr>
          <th scope="col">Target</th>
          <th scope="col">Field</th>
          <th scope="col">Prior → New</th>
          <th scope="col">Method</th>
          <th scope="col">Reason</th>
          <th scope="col">Actor</th>
          <th scope="col">Channel</th>
          <th scope="col">Applied at</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <Fragment key={row.id}>
            <tr>
              <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                {targetHref(row.targetType) ? (
                  <EntityRef kind="record" id={row.targetId} name={row.targetLabel ?? row.targetType} />
                ) : (
                  row.targetLabel ?? row.targetType
                )}
              </td>
              <td>{row.fieldName}</td>
              <td>
                <strong>
                  {row.priorValueDisplay} → {row.newValueDisplay}
                </strong>
              </td>
              <td>
                <StatusPill variant={METHOD_VARIANT[row.method] ?? 'neutral'} label={methodLabel(row.method)} />
              </td>
              <td style={{ maxWidth: 260 }}>{row.reason}</td>
              <td style={{ fontSize: 'var(--iw-size-xs)' }}>{row.actorLabel ?? 'System'}</td>
              <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>{row.channel ?? '—'}</td>
              <td style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                {new Date(row.appliedAt).toLocaleString()}
              </td>
            </tr>
          </Fragment>
        ))}
      </tbody>
    </table>
  )
}
